import * as Sentry from '@sentry/react';
import { BasePlugin, type PluginOpts } from '@uppy/core';
import type { AssemblyResponse } from '@uppy/transloadit';
import type Q from 'q';

import {
  failAssetCreationAttempt,
  succeedAssetCreationAttempt,
} from 'bundles/asset-admin/utils/AssetCreationAttemptsApiUtils';

import _t from 'i18n!nls/asset-admin';

import { type TBody, type TMeta, type UppyFile, type UppyWithMeta, isAssetAssembly } from '../types/uppy';

type AssetCreationPluginOptions = PluginOpts & { id?: string; enabled: boolean };

export default class AssetCreationPlugin extends BasePlugin<AssetCreationPluginOptions, TMeta, TBody> {
  declare opts: AssetCreationPluginOptions;

  assetCreationPromiseMap: { [attemptId: string]: Q.Promise<void> } = {};

  constructor(uppy: UppyWithMeta, opts?: AssetCreationPluginOptions) {
    super(uppy, opts);
    this.id = opts?.id ?? 'AssetCreationPlugin';
    this.type = 'AssetCreationPlugin';
    this.uppy = uppy;
    if (opts) {
      this.opts = opts;
    }
  }

  // enqueue an asset creation promise to wait for before completing the upload
  succeedAssetCreationAttempt = (assembly: AssemblyResponse) => {
    if (assembly.ok === 'REQUEST_ABORTED' || !isAssetAssembly(assembly)) {
      const attemptId = assembly?.fields?.attemptId;
      if (attemptId) {
        // we can only update the asset status in the backend if we have a valid attemptId to send the request for
        failAssetCreationAttempt(assembly);
      }

      return this.uppy.emit('assetCreation:error', Error('Failed to create asset'), assembly);
    }

    assembly.results.virus_scanned?.forEach((file) => {
      const attemptId = file.user_meta.attemptId;
      const uppyFileSource = this.uppy.getFiles().find((file) => file.meta.attemptId === attemptId)?.source || '';

      if (!attemptId) {
        this.uppy.emit('assetCreation:error', Error('Failed to create asset'), assembly);
        return;
      }

      this.assetCreationPromiseMap[attemptId] = succeedAssetCreationAttempt(attemptId, file, uppyFileSource, assembly)
        .then(() => {
          const assetIds = [attemptId];
          this.uppy.log(`[assetCreation:complete]: ${assetIds} ${assembly}`);
          this.uppy.emit('assetCreation:complete', assetIds, assembly);
        })
        .catch((error) => {
          // [fe-tech-debt] CP-3095: we should update our asset upload flow to handle cancelled uploads correctly. For now, when a user cancels their upload, the assembly still returns an ok status so we ignore logging it https://transloadit.com/docs/api/#ok-codes
          if (assembly.ok === 'ASSEMBLY_CANCELED') {
            return;
          }

          const errorMessage = `${error.status} - ${error.responseText}`;
          this.uppy.log(
            `[assetCreation:error]: error - ${errorMessage}
          assembly - ${JSON.stringify(assembly)}`,
            'error'
          );

          this.uppy.log(`[assetCreation:error]: ${error} ${assembly}`, 'error');
          this.uppy.emit('assetCreation:error', error, assembly);
          Sentry.captureException('Asset Creation Failure', {
            extra: { error: errorMessage, assembly: JSON.stringify(assembly) },
          });
        });
    });
  };

  waitForAssetsToBeCreated = async (fileIds: Array<string>) => {
    const files = this.uppy.getFiles().filter((file) => fileIds.includes(file.id));

    await Promise.all(
      files.map(async (file) => {
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: _t('Saving Asset to Coursera...'),
        });
        try {
          const attemptId = (file as UppyFile & { meta: { attemptId: string } })?.meta?.attemptId;
          const assetCreationPromise = this.assetCreationPromiseMap[attemptId];
          if (!assetCreationPromise) {
            throw new Error(_t('We encountered an error saving your asset'));
          }
          await assetCreationPromise;
        } catch (error) {
          this.uppy.setState({ error });
        }
        this.uppy.emit('postprocess-complete', file);
      })
    );
  };

  install = () => {
    if (this.opts.enabled) {
      if (this.succeedAssetCreationAttempt) {
        this.uppy.on('transloadit:complete', this.succeedAssetCreationAttempt);
      }
      this.uppy.addPostProcessor(this.waitForAssetsToBeCreated);
    }
  };

  uninstall = () => {
    if (this.opts.enabled) {
      if (this.succeedAssetCreationAttempt) {
        this.uppy.off('transloadit:complete', this.succeedAssetCreationAttempt);
      }
      this.uppy.removePostProcessor(this.waitForAssetsToBeCreated);
    }
  };
}
